/**
 * Send-window rules: when a campaign is allowed to send, and how to say that
 * in a sentence.
 *
 * Shared because both sides need the SAME answer. The engine
 * (server/services/blast.ts, processCampaignTick and processNewsletterTick)
 * decides whether to send right now; the composer has to tell the user
 * whether pressing Send means "going out now" or "going out Monday". A
 * newsletter inherits send_window_start 8, send_window_end 18 and a null
 * send_days (meaning Monday to Friday) and exposes no editor for any of it,
 * so launching at 7pm on a Friday does nothing at all until Monday 8am. That
 * is fine behaviour and a terrible surprise, which is what
 * describeSendWindow() exists to prevent.
 *
 * Deliberately duplicated from NOTHING: server/lib/timezone.ts's
 * getZonedParts is server-side and does more than this needs, and a second
 * copy of the predicate in the client is exactly how the two would come to
 * disagree. inSendWindow in blast.ts is now a thin adapter over
 * isInSendWindow below.
 */

/** Monday to Friday. A null/empty send_days means this. */
export const DEFAULT_SEND_DAYS = [1, 2, 3, 4, 5];

export const DEFAULT_SEND_TIMEZONE = "America/Chicago";

export interface SendWindow {
  sendWindowStart: number;
  sendWindowEnd: number;
  /** 0 = Sunday. Null or empty means DEFAULT_SEND_DAYS. */
  sendDays: number[] | null;
  timezone: string | null;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

const SHORT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function zonedHourAndWeekday(now: Date, timeZone: string): { hour: number; weekday: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    weekday: "short",
    hour12: false,
  }).formatToParts(now);
  const out: Record<string, string> = {};
  for (const p of parts) out[p.type] = p.value;
  return {
    // Some ICU versions report midnight as "24" under hour12: false.
    hour: out.hour === "24" ? 0 : Number(out.hour),
    weekday: WEEKDAY_INDEX[out.weekday] ?? 0,
  };
}

export function isInSendWindow(w: SendWindow, now: Date = new Date()): boolean {
  const { hour, weekday } = zonedHourAndWeekday(now, w.timezone || DEFAULT_SEND_TIMEZONE);
  const days = w.sendDays && w.sendDays.length ? w.sendDays : DEFAULT_SEND_DAYS;
  if (!days.includes(weekday)) return false;
  return hour >= w.sendWindowStart && hour < w.sendWindowEnd;
}

/** "8am", "6pm", "noon", "midnight". */
function hourLabel(h: number): string {
  const norm = ((Math.trunc(h) % 24) + 24) % 24;
  if (norm === 0) return "midnight";
  if (norm === 12) return "noon";
  return norm < 12 ? `${norm}am` : `${norm - 12}pm`;
}

function daysLabel(sendDays: number[] | null): string {
  const days = [...(sendDays && sendDays.length ? sendDays : DEFAULT_SEND_DAYS)].sort((a, b) => a - b);
  const key = days.join(",");
  if (key === "1,2,3,4,5") return "weekdays";
  if (key === "0,1,2,3,4,5,6") return "every day";
  if (key === "0,6") return "weekends";
  return days.map((d) => SHORT_DAY[d] ?? "").filter(Boolean).join(", ");
}

/**
 * "Central Time" for America/Chicago. longGeneric avoids the CST/CDT flip,
 * which is noise in a sentence about business hours. Older runtimes reject
 * the option with a RangeError, hence the fallbacks; the zone id itself is
 * the last resort and is still true, just ugly.
 */
export function timezoneLabel(timeZone: string, now: Date = new Date()): string {
  for (const style of ["longGeneric", "short"] as const) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: style }).formatToParts(now);
      const name = parts.find((p) => p.type === "timeZoneName")?.value;
      if (name) return name;
    } catch {
      // Try the next style.
    }
  }
  return timeZone;
}

/** "weekdays 8am to 6pm Central Time" — meant to sit inside a sentence. */
export function describeSendWindow(w: SendWindow, now: Date = new Date()): string {
  const tz = w.timezone || DEFAULT_SEND_TIMEZONE;
  return `${daysLabel(w.sendDays)} ${hourLabel(w.sendWindowStart)} to ${hourLabel(w.sendWindowEnd)} ${timezoneLabel(tz, now)}`;
}
